export interface LagAnalysisDisplayPoint {
  lagMonths: number;
  correlation: number | null;
  samples: number;
}

export interface LagAnalysisDisplayWindow {
  id: string;
  label: string;
  startLagMonths: number;
  endLagMonths: number;
  validPoints: number;
  sameSignCount: number;
  dominantSign: "positive" | "negative" | "mixed" | "insufficient";
  bestLagMonths: number | null;
  bestCorrelation: number | null;
  minimumSamples: number | null;
}

export interface LagAnalysisDisplayScan {
  id: string;
  label: string;
  points: LagAnalysisDisplayPoint[];
  bestLagMonths: number | null;
  bestCorrelation: number | null;
  windows?: LagAnalysisDisplayWindow[];
  positiveMeaning?: string;
  negativeMeaning?: string;
  note: string;
  status?: "ready" | "limited" | "insufficient";
}

function f(v: number | null) {
  return v == null ? "N/A" : v.toFixed(2);
}

function statusLabel(status: LagAnalysisDisplayScan["status"]) {
  if (status === "ready") return "표본 충분";
  if (status === "limited") return "표본 적음";
  if (status === "insufficient") return "표본 부족";
  return null;
}

function windowRange(window: LagAnalysisDisplayWindow) {
  return window.startLagMonths === window.endLagMonths
    ? `${window.startLagMonths}M`
    : `${window.startLagMonths}~${window.endLagMonths}M`;
}

function windowDirection(window: LagAnalysisDisplayWindow) {
  if (window.dominantSign === "positive") return "양(+) 방향 우세";
  if (window.dominantSign === "negative") return "음(-) 방향 우세";
  if (window.dominantSign === "mixed") return "부호 엇갈림";
  return "판단 보류";
}

function windowMeaning(scan: LagAnalysisDisplayScan, window: LagAnalysisDisplayWindow) {
  if (window.dominantSign === "positive") return scan.positiveMeaning ?? "두 변화가 같은 방향으로 움직이는 경향입니다.";
  if (window.dominantSign === "negative") return scan.negativeMeaning ?? "두 변화가 반대 방향으로 움직이는 경향입니다.";
  if (window.dominantSign === "mixed") return "시차별 부호가 섞였거나, 다수 부호와 가장 강한 r의 부호가 달라 한 방향의 전이로 단정하기 어렵습니다.";
  return "비교 가능한 r이 충분하지 않아 이 전이구간은 아직 판단하지 않습니다.";
}

export default function LagAnalysisTable({ scans, emptyText }: { scans: LagAnalysisDisplayScan[]; emptyText?: string }) {
  if (!scans.length) {
    return <div className="empty-state">{emptyText ?? "비교 가능한 월별 계열이 충분히 확인되면 lead/lag 분석을 자동 계산합니다."}</div>;
  }
  return (
    <>
      <div className="lag-reading-guide"><b>쉽게 읽기</b><span>r+ = 두 변화가 같은 방향 · r− = 반대 방향 · +kM = 앞 지표가 k개월 먼저</span><span>한 달의 최고 r보다 여러 lag에서 같은 부호가 이어지는지를 먼저 봅니다.</span></div>
      <div className="lag-grid">
      {scans.map((scan) => (
        <article className="lag-card" key={scan.id}>
          <div className="lag-card__header">
            <div>
              <strong>{scan.label}</strong>
              <small>월간 변화량 Pearson r · +kM는 앞 지표가 k개월 먼저라는 뜻</small>
              {statusLabel(scan.status) && <span className={`lag-status lag-status--${scan.status}`}>{statusLabel(scan.status)}</span>}
            </div>
          </div>

          {scan.windows?.length ? (
            <div className="lag-window-grid">
              {scan.windows.map((window) => (
                <div className={`lag-window lag-window--${window.dominantSign}`} key={window.id}>
                  <div className="lag-window__top">
                    <strong>{window.label}</strong>
                    <span>{windowRange(window)}</span>
                  </div>
                  <b>{windowDirection(window)}</b>
                  <small>
                    {window.bestLagMonths == null
                      ? "구간 내 유효 r 없음"
                      : `구간 내 최대 |r| ${f(window.bestCorrelation)} @ ${window.bestLagMonths}M`}
                  </small>
                  <small>{window.validPoints ? `같은 부호 ${window.sameSignCount}/${window.validPoints}` : "비교 가능한 lag 없음"}</small>
                  <p>{windowMeaning(scan, window)}</p>
                </div>
              ))}
            </div>
          ) : null}

          <details className="lag-details">
            <summary>전체 시차별 r 보기</summary>
            <div className="lag-details__note">
              전체 구간의 최대 |r|는 <b>{scan.bestLagMonths == null ? "N/A" : `${scan.bestLagMonths}M / r=${f(scan.bestCorrelation)}`}</b>이지만, 한 점만으로 실제 전달시차를 정하지 않습니다.
            </div>
            <div className="lag-points" style={{ gridTemplateColumns: `repeat(${Math.min(7, Math.max(4, scan.points.length))}, minmax(0, 1fr))` }}>
              {scan.points.map((p) => (
                <div key={p.lagMonths} className={p.lagMonths === scan.bestLagMonths ? "is-best" : ""}>
                  <span>{p.lagMonths}M</span><b>{f(p.correlation)}</b><small>n={p.samples}</small>
                </div>
              ))}
            </div>
          </details>
          <p>{scan.note}</p>
        </article>
      ))}
      </div>
    </>
  );
}
